import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { PersonaContext } from "../context/PersonaContext";
import Header from "../components/Header";
import StatusBar from "../components/StatusBar";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import { getQuestions, retryApiCall } from "../services/api";
import HelpTooltip from "../components/HelpTooltip";

const QuestionPage = () => {
  const {
    selectedDataset,
    selectedDatasetName,
    selectedSectors,
    selectedQuestions,
    setSelectedQuestions,
  } = useContext(PersonaContext);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groupedResult, setGroupedResult] = useState({});
  const navigate = useNavigate();
  const [validationError, setValidationError] = useState("");

  const fetchQuestions = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await retryApiCall(getQuestions, [
        selectedDataset,
        selectedSectors,
      ]);
      setQuestions(data);

      const grouped = data.reduce((acc, item) => {
        const sector = item.sector;
        if (!acc[sector]) {
          acc[sector] = [];
        }
        acc[sector].push({
          id: item.id,
          text: item.text,
          question_actual: item.question_actual,
          topic: item.topic,
          is_choice: item.is_choice,
          answer_format: item.answer_format,
        });
        return acc;
      }, {});

      setGroupedResult(grouped);
    } catch (error) {
      setError("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [selectedDataset, selectedSectors]);

  const handleQuestionSelection = (questionId) => {
    setSelectedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
    setValidationError("");
  };

  const handleSelectAllForSector = (sector) => {
    const sectorQuestions = groupedResult[sector].map(
      (question) => question.id
    );
    setSelectedQuestions((prev) => [...new Set([...prev, ...sectorQuestions])]);
    setValidationError("");
  };

  const handleUnselectAllForSector = (sector) => {
    const sectorQuestions = groupedResult[sector].map(
      (question) => question.id
    );
    setSelectedQuestions((prev) =>
      prev.filter((id) => !sectorQuestions.includes(id))
    );
    setValidationError("");
  };

  const handleNextClick = () => {
    if (selectedQuestions.length === 0) {
      setValidationError("Please select at least one question");
      return;
    }
    navigate("/ethnicity");
  };

  return (
    <div className="question-page">
      <Header />
      <StatusBar currentStep={3} />
      <h2>Select Topics</h2>
      <p>Select Topics that are relevant to your persona creation.</p>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {error && (
            <div className="error-container">
              <ErrorMessage message={error} />
              <button onClick={fetchQuestions} className="retry-button">
                Try Again
              </button>
            </div>
          )}
          {validationError && <ErrorMessage message={validationError} />}

          <h4>Dataset: {selectedDatasetName.toUpperCase()} Dataset</h4>
          <div>
            {Object.keys(groupedResult).map((sector) => (
              <div key={sector}>
                <h3>{sector}</h3>
                <div className="question-buttons">
                  <button
                    onClick={() => handleSelectAllForSector(sector)}
                    className="link-button"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => handleUnselectAllForSector(sector)}
                    className="link-button"
                  >
                    Unselect All
                  </button>
                </div>
                <ul className="question-list">
                  {groupedResult[sector].map((question) => (
                    <li key={question.id}>
                      <input
                        type="checkbox"
                        id={`question-${question.id}`}
                        name="question"
                        value={question.id}
                        checked={selectedQuestions.includes(question.id)}
                        onChange={() => handleQuestionSelection(question.id)}
                      />
                      <label htmlFor={`question-${question.id}`}>
                        {question.topic}
                      </label>
                      <HelpTooltip
                        sections={[
                          {
                            title: "Question asked at the survey",
                            content: question.question_actual.replace(
                              /^Q\d+[a-zA-Z]?\. ?/,
                              ""
                            ),
                          },
                        ]}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="navigation-buttons">
            <button
              onClick={() => navigate("/dataset")}
              className="back-button"
            >
              Back
            </button>
            <button onClick={handleNextClick} className="next-button">
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QuestionPage;
